import { AlertTriangle, CheckCircle2, Loader2, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, ModalError } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/toast';
import { ApiError } from '@/lib/api';
import {
  MAKS_KART_NARAZ,
  parsujListeKart,
  podgladUsunieciaKart,
  usunKarty,
  type PodgladUsunieciaKart,
  type WierszUsuniecia,
  type WynikUsunieciaKart,
} from '@/lib/employees-api';
import { useI18n } from '@/lib/i18n';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  USUWANIE KART Z AUTOMATÓW (29.09.2026) — lista wklejona, jedna karta w wierszu.
 *
 *  ⚠️⚠️ ZAMÓWIONE: usunięcie na stronie wywala osobę z baz SPS (master i automaty), a w Vendi osoba
 *  zostaje z oznaczeniem i historią wydań. Pole „trwale" usuwa także z Vendi — i wtedy okno MUSI
 *  powiedzieć, ile wydań zostanie bez osoby („co powinno pokazać, że taka akcja osieroci X wpisów").
 *
 *  ⚠️ NAJPIERW PODGLĄD, POTEM ZLECENIE: te same reguły liczy serwer przy zapisie, podgląd jest
 *  zgodą człowieka na liczby, które widzi — nie dowodem.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
/** Ile wierszy podglądu rysujemy — przy pięciu tysiącach kart lista ma dać się przewinąć, nie zawiesić. */
const WIERSZY_W_PODGLADZIE = 500;

const WARIANT_STANU: Record<WierszUsuniecia['stan'], 'destructive' | 'neutral' | 'warning'> = {
  DO_USUNIECIA: 'destructive',
  JUZ_USUNIETA: 'neutral',
  NIEZNANA: 'warning',
};

export function UsuwanieKart({
  groupId,
  numery: poczatkowe,
  trwale: trwalePoczatkowo = false,
  onClose,
  onUsunieto,
}: {
  groupId: string;
  /** Karty wskazane z wiersza albo z karty osoby — okno od razu pokazuje podgląd. */
  numery?: readonly string[];
  /** Otwarte z osoby już usuniętej z automatów — zostaje tylko trwałe usunięcie z Vendi. */
  trwale?: boolean;
  onClose: () => void;
  onUsunieto: () => void;
}) {
  const { t } = useI18n();
  const toast = useToast();
  const [tekst, setTekst] = useState(() => (poczatkowe ?? []).join('\n'));
  const numery = useMemo(() => parsujListeKart(tekst), [tekst]);
  const [podglad, setPodglad] = useState<PodgladUsunieciaKart | null>(null);
  const [trwale, setTrwale] = useState(trwalePoczatkowo);
  const [odpiecie, setOdpiecie] = useState(false);
  const [wynik, setWynik] = useState<WynikUsunieciaKart | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pracuje, setPracuje] = useState(false);

  const sprawdz = async (): Promise<void> => {
    setPracuje(true);
    setError(null);
    try {
      setPodglad(await podgladUsunieciaKart(groupId, numery));
      setOdpiecie(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setPracuje(false);
    }
  };

  /** Otwarte z konkretną kartą — podgląd od razu, bez klikania „Sprawdź". */
  const sprawdzone = useRef(false);
  useEffect(() => {
    if (sprawdzone.current || (poczatkowe ?? []).length === 0) return;
    sprawdzone.current = true;
    void sprawdz();
  });

  const s = podglad?.sumy;
  const coRobic = s !== undefined && (s.doUsuniecia > 0 || (trwale && s.juzUsunietych > 0));
  const wymagaZgody = trwale && (s?.wydanDoOdpiecia ?? 0) > 0;
  const blokadaWersji = podglad !== null && (s?.doUsuniecia ?? 0) > 0 && podglad.starszeAgenty.length > 0;
  const mozna = coRobic && !blokadaWersji && (!wymagaZgody || odpiecie) && !pracuje;
  const zaDuzo = numery.length > MAKS_KART_NARAZ;

  const usun = async (): Promise<void> => {
    setPracuje(true);
    setError(null);
    try {
      const r = await usunKarty({ groupId, numery, trwale, potwierdzamOdpiecie: odpiecie });
      setWynik(r);
      onUsunieto();
      if (r.blad === null) toast.success(t('karty.zakolejkowano'));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setPracuje(false);
    }
  };

  const liczba = (klucz: string, n: number) => t(klucz).replace('{n}', String(n));

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent title={t('karty.tytul')} description={t('karty.opis')} className="w-[min(56rem,calc(100vw-2rem))]">
        {wynik !== null ? (
          <div className="space-y-2 text-sm">
            <p className="flex items-center gap-2">
              <CheckCircle2 className="size-4 text-[var(--success)]" />
              {t('karty.wynik').replace('{n}', String(wynik.oznaczonych)).replace('{k}', String(wynik.opIds.length))}
            </p>
            {wynik.zablokowanych > 0 && <p>{liczba('karty.wynikBlokada', wynik.zablokowanych)}</p>}
            {wynik.usunietychTrwale > 0 && <p>{liczba('karty.wynikTrwale', wynik.usunietychTrwale)}</p>}
            {wynik.blad !== null && <p className="text-[var(--destructive)]">{wynik.blad}</p>}
          </div>
        ) : podglad === null ? (
          <div className="space-y-2">
            <label className="flex flex-col gap-1 text-xs text-muted-foreground">
              {t('karty.lista')}
              <textarea
                value={tekst}
                onChange={(e) => setTekst(e.target.value)}
                rows={12}
                spellCheck={false}
                placeholder={t('karty.placeholder')}
                className="w-full rounded-md border border-border bg-background px-2 py-1.5 font-mono text-sm text-foreground"
              />
            </label>
            <p className="text-xs text-muted-foreground">{liczba('karty.ile', numery.length)}</p>
            {zaDuzo && <p className="text-xs text-[var(--destructive)]">{liczba('karty.zaDuzo', MAKS_KART_NARAZ)}</p>}
          </div>
        ) : (
          <div className="space-y-3 text-sm">
            <div className="flex flex-wrap gap-1.5">
              <Badge variant="destructive">{liczba('karty.doUsuniecia', s!.doUsuniecia)}</Badge>
              {s!.juzUsunietych > 0 && <Badge variant="neutral">{liczba('karty.juzUsuniete', s!.juzUsunietych)}</Badge>}
              {s!.nieznanych > 0 && <Badge variant="warning">{liczba('karty.nieznane', s!.nieznanych)}</Badge>}
              {podglad.powtorzen > 0 && <Badge variant="neutral">{liczba('karty.powtorzenia', podglad.powtorzen)}</Badge>}
            </div>

            {s!.doUsuniecia > 0 && (
              <div className="space-y-1 rounded-md border border-border p-2 text-xs">
                <p>
                  <span className="font-medium">{t('karty.zniknie')}: </span>
                  {[
                    liczba('karty.przypisan', s!.przypisan),
                    liczba('karty.grup', s!.grup),
                    liczba('karty.mpk', s!.mpk),
                    liczba('karty.operacji', s!.operacji),
                    liczba('karty.limitow', s!.limitow),
                    liczba('karty.dostepow', s!.dostepow),
                    liczba('karty.dostawcow', s!.dostawcow),
                  ].join(' · ')}
                </p>
                <p className="text-muted-foreground">{t('karty.bazy').replace('{lista}', podglad.wezly.join(', '))}</p>
                {podglad.operacji > 1 && <p className="text-muted-foreground">{liczba('karty.operacjiWCzesciach', podglad.operacji)}</p>}
              </div>
            )}

            {blokadaWersji && (
              <p className="flex items-start gap-1.5 text-xs text-[var(--destructive)]">
                <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
                {t('karty.starsze')
                  .replace('{wersja}', podglad.wymaganaWersja)
                  .replace('{lista}', podglad.starszeAgenty.map((a) => `${a.nodeId} (${a.version ?? '—'})`).join(', '))}
              </p>
            )}

            <div className="max-h-56 overflow-auto rounded-md border border-border">
              <table className="w-full border-collapse text-xs">
                <thead className="sticky top-0 bg-card">
                  <tr className="text-left text-muted-foreground">
                    <th className="px-2 py-1 font-normal">{t('karty.kolKarta')}</th>
                    <th className="px-2 py-1 font-normal">{t('karty.kolOsoba')}</th>
                    <th className="px-2 py-1 font-normal">{t('karty.kolStan')}</th>
                    <th className="px-2 py-1 text-right font-normal">{t('karty.kolAutomaty')}</th>
                    <th className="px-2 py-1 text-right font-normal">{t('karty.kolWydania')}</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Klucz z pozycją — serwer wycina powtórzenia, ale wiersz nie może zniknąć przez sam klucz. */}
                  {podglad.wiersze.slice(0, WIERSZY_W_PODGLADZIE).map((w, i) => (
                    <tr key={`${i}-${w.numer}`} className="border-t border-border/50">
                      <td className="px-2 py-0.5 font-mono">{w.numer}</td>
                      <td className="px-2 py-0.5">{w.nazwa ?? '—'}</td>
                      <td className="px-2 py-0.5">
                        <Badge variant={WARIANT_STANU[w.stan]}>{t(`karty.stan.${w.stan}`)}</Badge>
                      </td>
                      <td className="px-2 py-0.5 text-right tabular-nums">{w.przypisan}</td>
                      <td className="px-2 py-0.5 text-right tabular-nums">{w.wydan}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {podglad.wiersze.length > WIERSZY_W_PODGLADZIE && (
                <p className="px-2 py-1 text-xs text-muted-foreground">
                  {liczba('karty.iWiecej', podglad.wiersze.length - WIERSZY_W_PODGLADZIE)}
                </p>
              )}
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={trwale} onChange={(e) => setTrwale(e.target.checked)} />
              {t('karty.trwale')}
            </label>
            {wymagaZgody && (
              <label className="flex items-start gap-2 rounded-md border border-[var(--destructive)]/40 bg-[var(--destructive)]/5 p-2 text-xs">
                <input type="checkbox" checked={odpiecie} onChange={(e) => setOdpiecie(e.target.checked)} className="mt-0.5" />
                <span>{liczba('karty.odpiecie', s!.wydanDoOdpiecia)}</span>
              </label>
            )}
          </div>
        )}

        <ModalError>{error}</ModalError>

        <DialogFooter>
          {wynik !== null ? (
            <Button type="button" onClick={onClose}>
              {t('common.close')}
            </Button>
          ) : (
            <>
              <Button type="button" variant="outline" onClick={podglad === null ? onClose : () => setPodglad(null)}>
                {podglad === null ? t('common.cancel') : t('karty.zmienListe')}
              </Button>
              {podglad === null ? (
                <Button type="button" disabled={numery.length === 0 || zaDuzo || pracuje} onClick={() => void sprawdz()}>
                  {pracuje && <Loader2 className="animate-spin" />}
                  {t('karty.sprawdz')}
                </Button>
              ) : (
                <Button type="button" variant="destructive" disabled={!mozna} onClick={() => void usun()}>
                  {pracuje ? <Loader2 className="animate-spin" /> : <Trash2 />}
                  {s!.doUsuniecia > 0 ? t('karty.usun') : t('karty.usunTrwale')}
                </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
